import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getBackendURL } from '../../utils/backendURL';

const date = (value) => (value ? new Date(value).toLocaleString() : '—');
const delivery = {
  not_sent: 'Not sent',
  sending: 'Sending',
  accepted: 'SMTP accepted',
  failed: 'Send failed',
  unknown: 'Delivery unknown — check mail logs before resending',
};
const statuses = {
  pending: 'Pending',
  registered: 'Registered',
  cancelled: 'Cancelled',
  account_exists: 'Account exists — review user',
};
const reminder = {
  unknown: 'Delivery unknown',
  not_scheduled: 'Not scheduled',
  scheduled: 'Scheduled',
  sending: 'Sending',
  accepted: 'SMTP accepted',
  failed: 'Send failed',
};

const request = (method, path = '', body, options = {}) =>
  axios({
    method,
    url: `${getBackendURL()}/api/ops/client-invites${path}`,
    data: body,
    headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
    ...options,
  });

export default function OpsClientInvites({ onOpenUser }) {
  const [form, setForm] = useState({ email: '', company_name: '', language: 'en' });
  const [data, setData] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [message, setMessage] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError('');
    setData(null);
    request('get', '', undefined, {
      params: { skip: page * 20, limit: 20, search },
      signal: controller.signal,
    })
      .then(({ data: result }) => {
        if (!controller.signal.aborted) setData(result);
      })
      .catch(() => {
        if (!controller.signal.aborted)
          setLoadError('Unable to load client invitations. Refresh to try again.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [page, search, revision]);
  const act = async (key, path, body) => {
    setBusy(key);
    setError('');
    setMessage('');
    try {
      const { data: result } = await request('post', path, body);
      setMessage(
        result.cancelled
          ? 'Invitation cancelled. No further reminder will be sent.'
          : result.smtp_accepted
            ? 'Email accepted by SMTP. This does not confirm inbox delivery.'
            : 'SMTP did not confirm acceptance. The invitation is saved; review mail configuration before resending.',
      );
      if (key === 'create') setForm({ email: '', company_name: '', language: 'en' });
    } catch (failure) {
      const detail = failure.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Unable to complete the invitation action.');
    } finally {
      setBusy('');
      setRevision((value) => value + 1);
    }
  };
  return (
    <section className="space-y-5" aria-label="Client invitations">
      <div className="flex justify-between gap-3 items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Client Invites</h1>
          <p className="text-sm text-gray-600 mt-1">
            Invite a new customer to create their own organization. No account is created until they
            sign up.
          </p>
        </div>
        <button
          type="button"
          disabled={loading || Boolean(busy)}
          onClick={() => setRevision((value) => value + 1)}
          className="border rounded-lg px-4 py-2"
        >
          Refresh invites
        </button>
      </div>
      <p className="text-xs text-gray-500">
        One automatic reminder is scheduled 72 hours after the latest successful invitation send,
        while pending. A manual resend moves the deadline until the automatic reminder has been
        attempted. Registration is checked approximately every minute and on refresh.
      </p>
      {message && (
        <p role="status" className="p-3 rounded bg-blue-50 text-blue-800 text-sm">
          {message}
        </p>
      )}
      {(error || loadError) && (
        <p role="alert" className="p-3 rounded bg-red-50 text-red-800 text-sm">
          {error || loadError}
        </p>
      )}
      {data?.can_manage && (
        <form
          className="bg-white rounded-xl border p-5 flex flex-wrap items-end gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!busy) act('create', '', form);
          }}
        >
          <label className="text-sm flex-1 min-w-[200px]">
            Email
            <input
              required
              type="email"
              maxLength={254}
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
              className="block w-full border rounded mt-1 px-3 py-2"
            />
          </label>
          <label className="text-sm flex-1 min-w-[200px]">
            Company name
            <input
              required
              maxLength={150}
              value={form.company_name}
              onChange={(event) => setForm({ ...form, company_name: event.target.value })}
              className="block w-full border rounded mt-1 px-3 py-2"
            />
          </label>
          <label className="text-sm">
            Email language
            <select
              aria-label="Email language"
              value={form.language}
              onChange={(event) => setForm({ ...form, language: event.target.value })}
              className="block border rounded mt-1 px-3 py-2"
            >
              <option value="en">English</option>
              <option value="es">Español</option>
            </select>
          </label>
          <button
            disabled={Boolean(busy)}
            className="bg-blue-600 text-white rounded-lg px-4 py-2 disabled:opacity-50"
          >
            {busy === 'create' ? 'Sending…' : 'Send invite'}
          </button>
        </form>
      )}
      {data && !data.can_manage && (
        <p className="text-sm text-gray-500">
          Your OPS role can view invitations but cannot send or cancel them.
        </p>
      )}
      <form
        className="flex gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          setPage(0);
          setSearch(searchInput);
        }}
      >
        <input
          aria-label="Search client invites"
          placeholder="Search email or company"
          maxLength={150}
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          className="border rounded-lg p-2 flex-1 min-w-0"
        />
        <button className="border rounded-lg px-4 py-2">Search invites</button>
      </form>
      {loading && <p role="status">Loading invites…</p>}
      {data && (
        <div className="bg-white border rounded-xl overflow-x-auto">
          <table className="w-full text-sm text-left" aria-label="Client invites">
            <thead className="bg-gray-50">
              <tr>
                {[
                  'Customer',
                  'Status',
                  'Email sends',
                  'Automatic reminder',
                  'Registration',
                  'Actions',
                ].map((label) => (
                  <th key={label} className="p-4">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.items.map((invite) => (
                <tr key={invite.id}>
                  <td className="p-4">
                    <p className="font-medium">{invite.company_name}</p>
                    <p>{invite.email}</p>
                    <p className="text-xs text-gray-500">
                      {invite.language === 'es' ? 'Español' : 'English'} · Created{' '}
                      {date(invite.created_at)}
                    </p>
                  </td>
                  <td className="p-4">
                    <span
                      className={`inline-block rounded-full px-2 py-1 text-xs ${invite.status === 'registered' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}
                    >
                      {statuses[invite.status]}
                    </span>
                    <p className="text-xs text-gray-500 mt-2">{delivery[invite.delivery_status]}</p>
                  </td>
                  <td className="p-4">
                    <p>{invite.send_count} accepted</p>
                    <p className="text-xs text-gray-500">
                      First: {date(invite.first_sent_at)}
                      <br />
                      Last: {date(invite.last_sent_at)}
                    </p>
                    <details className="mt-2">
                      <summary className="cursor-pointer text-blue-700">Send history</summary>
                      <p className="text-xs text-gray-500 mt-1">Latest 50 events</p>
                      {invite.history.map((event, index) => (
                        <p className="text-xs my-1" key={index}>
                          {date(event.at)} · {event.kind} · {event.outcome} · {event.actor}
                        </p>
                      ))}
                    </details>
                  </td>
                  <td className="p-4 text-xs">
                    <p>
                      {invite.status === 'pending' ? reminder[invite.reminder_status] : 'Stopped'}
                    </p>
                    <p>Due: {date(invite.next_reminder_at)}</p>
                    <p>Attempted: {date(invite.reminder_attempted_at)}</p>
                    <p>SMTP accepted: {date(invite.reminder_sent_at)}</p>
                  </td>
                  <td className="p-4 text-xs">
                    <p>{date(invite.registered_at)}</p>
                    {invite.user_id && (
                      <button
                        className="mt-2 text-blue-700 hover:underline"
                        onClick={() => onOpenUser(invite.user_id)}
                      >
                        View user / organization
                      </button>
                    )}
                  </td>
                  <td className="p-4">
                    {data.can_manage && invite.status === 'pending' && (
                      <div className="flex flex-col gap-3">
                        <button
                          disabled={Boolean(busy) || invite.delivery_status === 'sending'}
                          className="text-blue-700 disabled:opacity-50"
                          onClick={() => act(invite.id, `/${invite.id}/resend`)}
                        >
                          Resend invite
                        </button>
                        <button
                          disabled={Boolean(busy) || invite.delivery_status === 'sending'}
                          className="text-red-700 disabled:opacity-50"
                          onClick={() => {
                            if (window.confirm(`Cancel invitation for ${invite.email}?`))
                              act(invite.id, `/${invite.id}/cancel`);
                          }}
                        >
                          Cancel invite
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!data.items.length && <p className="p-6 text-gray-500">No client invitations found.</p>}
        </div>
      )}
      {data && (
        <div className="flex justify-between items-center text-sm">
          <span>{data.total} invitations</span>
          <div className="flex gap-4">
            <button disabled={page === 0 || loading} onClick={() => setPage((value) => value - 1)}>
              Previous invites
            </button>
            <span>Page {page + 1}</span>
            <button
              disabled={(page + 1) * 20 >= data.total || loading}
              onClick={() => setPage((value) => value + 1)}
            >
              Next invites
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
